import React, { Reducer, useCallback, useReducer } from "react"

import Dialog, { DialogLevel } from "."

export { DialogLevel }

type DialogContextType = {
    closeDialog: (level?: DialogLevel) => void, 
    setComponent: (cmp: React.ReactElement, backdrop?: React.ReactElement, level?: DialogLevel, hideCloseIcon?: boolean) => void
}

const DialogContext = React.createContext<DialogContextType>({
  closeDialog: () => {},
  setComponent: () => {},
})

type ProviderState = {
  [key in DialogLevel]: { 
    component: React.ReactElement | undefined
    backdrop: React.ReactElement | undefined
    hideCloseIcon?: boolean
  }
}

export const DialogProvider: React.FunctionComponent = (props) => {
  const [state, setState] = useReducer<Reducer<ProviderState, Partial<ProviderState>>>(
    (state, newState) => ({...state, ...newState}),
    {
      [DialogLevel.Underlying]: { component: undefined, backdrop: undefined},
      [DialogLevel.Default]: { component: undefined, backdrop: undefined},
      [DialogLevel.Top]: { component: undefined, backdrop: undefined},
    }
  )

  const setComponent = useCallback((component, backdrop, level, hideCloseIcon) => {
    if (!level) { level = DialogLevel.Default }
    setState({
      [level]: {
        component, backdrop, hideCloseIcon,
      }
    })
  }, [])

  const closeDialog = useCallback((level?: DialogLevel) => {
    if (!level) { level = DialogLevel.Default }

    setComponent(undefined, undefined, level, undefined)
  }, [setComponent])

  return (
    <DialogContext.Provider value={{ closeDialog, setComponent }} {...props} >
      { 
        Object.values(DialogLevel)
          .filter(k => typeof k !== "string") // Object.keys() for enums return both values and keys
          .map((level) => {
            const { component, backdrop, hideCloseIcon } = state[level as unknown as DialogLevel]
            return (
              <React.Fragment key={level}>
                {component && <Dialog onClose={closeDialog} backdrop={backdrop} level={Number(level)} hideCloseIcon={hideCloseIcon}>
                    { component }
                </Dialog>}
              </React.Fragment>
            )
          })
      }
      {props.children}
    </DialogContext.Provider>
    )
}



export const useDialog = (): DialogContextType => {
  const context = React.useContext(DialogContext)
  if (context === undefined) {
    throw new Error('useModal must be used within a UserProvider')
  }

  return context
}